import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Clock, ArrowRight } from "lucide-react";
import { Reveal } from "../components/Reveal";
import CTABanner from "../components/CTABanner";
import { useSEO } from "../hooks/useSEO";
import { articles } from "../data/articles";

export default function Article() {
  const { slug } = useParams();
  const article = articles.find((a) => a.slug === slug);
  const others = articles.filter((a) => a.slug !== slug).slice(0, 2);

  useSEO({
    title: article ? `${article.title} | Conseils Embèk Group` : "Conseils jardinage | Embèk Group",
    description: article?.excerpt,
  });

  if (!article) {
    return (
      <div className="pt-40 pb-32 text-center">
        <p className="font-serif text-3xl text-forest">Article introuvable</p>
        <Link to="/conseils" className="btn-primary mt-8 inline-flex">Retour aux conseils</Link>
      </div>
    );
  }

  return (
    <>
      <article data-testid="article-page">
        <header className="relative h-[52vh] min-h-[380px] overflow-hidden">
          <img src={article.image} alt={article.title} className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-forest-dark via-forest-dark/50 to-forest-dark/30" />
          <div className="relative h-full max-w-4xl mx-auto px-4 sm:px-8 flex flex-col justify-end pb-14">
            <Link to="/conseils" data-testid="article-back-link" className="inline-flex items-center gap-2 text-sand/70 hover:text-sand text-sm font-medium transition-colors w-fit">
              <ArrowLeft size={15} /> Tous les conseils
            </Link>
            <h1 className="mt-4 font-serif text-3xl sm:text-5xl font-medium tracking-tight text-sand leading-[1.1]">
              {article.title}
            </h1>
            <p className="mt-4 flex items-center gap-2 text-[12px] uppercase tracking-[0.18em] font-semibold text-sand/70">
              {article.date}
              <span className="w-1 h-1 rounded-full bg-moss" />
              <span className="normal-case tracking-normal font-medium flex items-center gap-1.5">
                <Clock size={12} /> {article.readTime} de lecture
              </span>
            </p>
          </div>
        </header>

        <div className="max-w-3xl mx-auto px-4 sm:px-8 py-14 lg:py-20">
          <Reveal>
            <p className="font-serif italic text-xl sm:text-2xl text-forest/90 leading-relaxed">{article.excerpt}</p>
          </Reveal>
          <div className="mt-8 space-y-5">
            {article.content.map((block, i) =>
              block.type === "h2" ? (
                <Reveal key={i}>
                  <h2 className="pt-5 font-serif text-2xl sm:text-3xl font-medium text-forest">{block.text}</h2>
                </Reveal>
              ) : (
                <Reveal key={i}>
                  <p className="text-coal-muted font-light leading-relaxed text-[16px]">{block.text}</p>
                </Reveal>
              )
            )}
          </div>
        </div>

        {/* À lire aussi */}
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 pb-20">
          <h2 className="font-serif text-2xl sm:text-3xl font-medium text-forest">À lire aussi</h2>
          <div className="mt-8 grid md:grid-cols-2 gap-6">
            {others.map((a, i) => (
              <Reveal key={a.slug} delay={i * 0.08}>
                <Link
                  to={`/conseils/${a.slug}`}
                  className="group flex gap-5 bg-white rounded-2xl border border-forest/10 p-4 hover:shadow-lg hover:shadow-forest/10 transition-shadow"
                >
                  <img src={a.image} alt={a.title} loading="lazy" className="w-28 h-28 rounded-xl object-cover shrink-0" />
                  <div className="py-1">
                    <span className="text-[11px] uppercase tracking-[0.18em] font-semibold text-moss">{a.date}</span>
                    <h3 className="mt-1 font-serif text-lg font-medium text-forest leading-snug">{a.title}</h3>
                    <span className="mt-2 inline-flex items-center gap-1.5 text-[13px] font-semibold text-forest group-hover:gap-3 transition-all">
                      Lire l'article <ArrowRight size={13} />
                    </span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </article>

      <CTABanner />
    </>
  );
}
